import { Link2 } from 'lucide-react';

import { PortainerSsrfMode } from '@api/types.gen';

import { notifySuccess } from '@/portainer/services/notifications';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Widget } from '@@/Widget';

import { useAllowList, useUpdateAllowListMutation } from '../../queries';

import { AddEntryForm } from './AddEntryForm';
import { AllowListTable } from './AllowListTable';

const modeOptions = [
  { label: 'Off', value: PortainerSsrfMode.SSRF_MODE_OFF },
  { label: 'Audit', value: PortainerSsrfMode.SSRF_MODE_AUDIT },
  { label: 'Enforce', value: PortainerSsrfMode.SSRF_MODE_ENFORCE },
] as const;

export function SSRFPanel() {
  const allowListQuery = useAllowList();
  const mutation = useUpdateAllowListMutation();

  if (!allowListQuery.data) {
    return null;
  }

  const entries = allowListQuery.data.Entries ?? [];
  const mode = allowListQuery.data.Mode ?? PortainerSsrfMode.SSRF_MODE_OFF;

  return (
    <Widget id="ssrf-config">
      <Widget.Title icon={Link2} title="SSRF" />
      <Widget.Body>
        <div className="form-horizontal">
          <FormControl label="SSRF protection mode">
            <PortainerSelect
              value={mode}
              options={modeOptions}
              onChange={(value) =>
                mutation.mutate(
                  {
                    ...allowListQuery.data,
                    Mode: value ?? PortainerSsrfMode.SSRF_MODE_OFF,
                  },
                  {
                    onSuccess: () =>
                      notifySuccess('Success', 'SSRF mode updated'),
                  }
                )
              }
              disabled={mutation.isLoading}
              data-cy="ssrf-mode-selector"
            />
          </FormControl>
        </div>

        <div className="mb-3">
          <TextTip color="blue">
            Define the list of entries that are allowed for every request
            performed by the server
          </TextTip>
        </div>
        <AddEntryForm
          isLoading={mutation.isLoading}
          onSubmit={(entry) => handleSubmit([...entries, entry])}
        />

        <AllowListTable
          entries={entries}
          isLoading={mutation.isLoading}
          onDelete={(entry) => handleSubmit(entries.filter((e) => e !== entry))}
        />
      </Widget.Body>
    </Widget>
  );

  function handleSubmit(updatedEntries: string[]) {
    mutation.mutate(
      { ...allowListQuery.data, Entries: updatedEntries },
      {
        onSuccess: () => {
          notifySuccess('Success', 'allow list updated');
        },
      }
    );
  }
}
